import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import cn from "classnames";
import { isIOS, isAndroid } from "react-device-detect";
import useDeliveryRange from "@helpers/hooks/useDeliveryRange";
import useGooglePay from "@helpers/hooks/useGooglePay";
import { rememberOrderAccess } from "@helpers/utils/orderAccess";
import { useSecondModal } from "@helpers/hooks/useSecondModal";
import {useRecoilState, useRecoilValue} from "recoil";
import {
  netPriceState,
  imageCountState,
  framePricesState,
  defaultFramePriceState,
  shippingPriceState,
  handlingFeeState,
  creditsAppliedState,
  creditsBalanceState
} from "@atoms/priceCalc";
import { selectedShippingAddress, validCoupon, addressLinkState, imageFrameState, clientCroppedImagesState, personalAreaAuthState, giftCheckoutState } from "@atoms";
import { getPrices } from "../../../api/pricingService";
import { getCreditsBalance, applyCredits, removeCredits } from "@api";
import "./style.css";
import {OrderStatus} from "../../../constants/enums/orderStatus";
import InputCoupon from "../../components/InputCoupon";
import AmountCharged from "../../../helpers/AmountCharged";
import {imagesBlobState} from "@atoms";
import AddressLink from "../AddressLink";
import { getAbVariant, trackAbExposure } from "../../../api/visitTracking";
import { completeGiftCardOrder } from "../../../api/giftcard";

// Split test "cartTotalFont" (shared with the desktop drawer, sticky per visitor,
// 50/50): the סה"כ amount in Rubik (today) vs Ploni Yad — 22px on the mobile
// sheet (24px on desktop). Exposure fires when the sheet opens; the arm + device
// are stamped on the cart so the admin CRO page can attribute purchases per arm.
const CART_TOTAL_FONT_EXPERIMENT = "cartTotalFont";
const CART_TOTAL_FONT_ARMS = ["rubik", "ploni"];

const BASE_URL = process.env.REACT_APP_BASE_URL;
const TRANZILA_SCRIPT_URL = 'https://hf.tranzila.com/assets/js/thfapplepay.js';
// Apple's official SDK — registers the native <apple-pay-button> web component
// (Apple-recommended button: auto sized/centered/localized, crisp on any DPI).
const APPLE_PAY_SDK_URL = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';

function getFormValues() {
  const storedValues = localStorage.getItem("userShippingAddress");

  if (!storedValues)
    return {
      fullName: "",
      address1: "",
      address2: "",
      city: "",
      postalCode: "",
      phone: "",
      email: "",
      fromValidate: false,
    };
  return JSON.parse(storedValues);
}

// Helper function to check if address is complete
const isAddressComplete = (address) => {
  if (!address) return false;
  
  // Helper function to check if a string field is valid (not empty after trimming)
  const isValidField = (field) => {
    return typeof field === 'string' && field.trim().length > 0;
  };
  
  // Check if number fields are valid
  const isValidNumber = (field) => {
    return field !== undefined && field !== null && field !== '';
  };
  
  // Check all required fields
  return (
    isValidField(address.fullName) && 
    isValidField(address.phoneNumber) && 
    isValidField(address.email) && 
    isValidField(address.address) && 
    isValidField(address.addressDetails) && 
    // zipCode is now optional
    isValidField(address.city)
  );
};

const CheckoutMobile = (props) => {
  const navigate = useNavigate();
  const { style } = props;
  const netPrice = useRecoilValue(netPriceState);
  const imageCount = useRecoilValue(imageCountState);
  const [numberOfImages,] = useState(5);
  const imagesBlob = useRecoilValue(imagesBlobState);
  const [shippingAddressFormValues,] = useState(getFormValues);
  const selectedAddress = useRecoilValue(selectedShippingAddress);
  const selectedCoupon = useRecoilValue(validCoupon);
  const frameChoose = useRecoilValue(imageFrameState);
  const [framePrices, setFramePrices] = useRecoilState(framePricesState);
  const [defaultFramePrice, setDefaultFramePrice] = useRecoilState(defaultFramePriceState);
  const shippingPrice = useRecoilValue(shippingPriceState);
  const handlingFee = useRecoilValue(handlingFeeState);
  const [auth, setAuth] = useRecoilState(personalAreaAuthState);
  const [creditsApplied, setCreditsApplied] = useRecoilState(creditsAppliedState);
  const [creditsBalance, setCreditsBalance] = useRecoilState(creditsBalanceState);
  const modal = useSecondModal();

  // GIFT-CARD mode: the sheet charges the server-priced gift amount against
  // the GC- order — no address/shipping/coupon/credits, order pre-created.
  const [giftCheckout, setGiftCheckout] = useRecoilState(giftCheckoutState);
  const giftMode = !!giftCheckout;

  const totalFontVariant = getAbVariant(CART_TOTAL_FONT_EXPERIMENT, CART_TOTAL_FONT_ARMS);
  useEffect(() => {
    // Gift-card checkout reuses this sheet but is a different funnel — keep it out.
    if (props.isOpen && !giftMode) {
      trackAbExposure(CART_TOTAL_FONT_EXPERIMENT, totalFontVariant, localStorage.getItem("sessionId"));
    }
  }, [props.isOpen, giftMode]); // eslint-disable-line react-hooks/exhaustive-deps -- totalFontVariant is sticky per visitor

  // Google Pay (Tranzila Hosted Fields) — eligibility fires via `googleIsEnable`.
  // Reuses the same preloaded order + handshake token (thtk) as Apple Pay.
  const { googlePayEligible, chargeGooglePay } = useGooglePay();

  // Apple Pay states
  const [isApplePaySupported, setIsApplePaySupported] = useState(false);
  const [isApplePayScriptLoaded, setIsApplePayScriptLoaded] = useState(false);
  const scriptRef = useRef(null);
  // Re-entrancy guard: Apple Pay allows only one active payment session per page.
  // A double-tap would otherwise call hfChargeApple twice and throw
  // "Page already has an active payment session." Real taps are separated by a
  // re-render, so state reflects the active session by the second tap.
  const [applePaySessionActive, setApplePaySessionActive] = useState(false);

  // Pre-fetched Apple Pay data (needed for synchronous hfChargeApple call)
  const [applePayReady, setApplePayReady] = useState(false);
  // The native <apple-pay-button> is only paintable once Apple's SDK has registered the
  // custom element. Track that so we show a spinner (not an empty box) until then.
  const [appleSdkLoaded, setAppleSdkLoaded] = useState(false);
  const sdkScriptRef = useRef(null);
  const [preloadedOrderId, setPreloadedOrderId] = useState(null);
  const [preloadedHandshakeToken, setPreloadedHandshakeToken] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(false);

  // Wolt-style Apple Pay feedback overlay: null | 'processing' | 'success' | 'error'
  const [applePayFeedback, setApplePayFeedback] = useState(null);
  // Guards the async outcome: false while a sheet is open, true once resolved/cancelled.
  const applePayResolvedRef = useRef(true);
  const applePayWatchdogRef = useRef(null);
  const feedbackTimerRef = useRef(null);
  const dismissWatchCleanupRef = useRef(null);

  // Get the current frame type from localStorage or use the one from Recoil state
  const currentFrameType = frameChoose || localStorage.getItem("frameSelected") || "classic";

  // Delivery date picks the lead-time for the selected frame (falls back to default).
  const { cartDate } = useDeliveryRange(currentFrameType);

  // Get the price for the current frame type, or fall back to the default price
  const framePrice = framePrices[currentFrameType] || defaultFramePrice;

  // Frame type mapping for display
  const frameTypeDisplay = {
    "classic": { text: "קלאסי", icon: "/assets/file/images/d1.png" },
    "bold": { text: "נועז", icon: "/assets/file/images/d2.png" },
    "ever": { text: "נקי", icon: "/assets/file/images/d3.png" },
    "clean": { text: "אותנטי", icon: "/assets/file/images/d4.png" },
    "village": { text: "כפרי", icon: "/assets/file/images/d5.png" },
    "nordic": { text: "נורדי", icon: "/assets/file/images/d6.png" }
  };

  const currentFrameDisplay = frameTypeDisplay[currentFrameType] || frameTypeDisplay["classic"];

  const amountBeforeCredits = giftMode ? giftCheckout.amount : AmountCharged(
    imageCount + imagesBlob.length,
    numberOfImages,
    netPrice + (imagesBlob.length * framePrice),
    shippingPrice,
    selectedCoupon,
    handlingFee,
    0
  );
  const amountCharged = giftMode ? giftCheckout.amount : AmountCharged(
    imageCount + imagesBlob.length,
    numberOfImages,
    netPrice + (imagesBlob.length * framePrice),
    shippingPrice,
    selectedCoupon,
    handlingFee,
    creditsApplied
  );

  // Gift mode needs no shipping address — treat it as satisfied everywhere.
  const addressOk = giftMode || isAddressComplete(selectedAddress);

  // A gift card in the coupon slot that covers the whole photo order: nothing to
  // pay, so the main button completes the order instead of opening payment.
  const giftCovers = !giftMode && !!selectedCoupon?.giftCard && amountCharged === 0;
  const [giftOrderBusy, setGiftOrderBusy] = useState(false);
  const [giftOrderError, setGiftOrderError] = useState("");
  const handleGiftCardOrder = async () => {
    if (giftOrderBusy) return;
    setGiftOrderBusy(true);
    setGiftOrderError("");
    try {
      rememberOrderAccess(); // the success page needs these after localStorage.clear()
      const oid = await completeGiftCardOrder({
        ...selectedAddress,
        uid: localStorage.getItem("uniqueUserId"),
        sessionId: localStorage.getItem("sessionId"),
        imagesPayed: imageCount + imagesBlob.length,
        frameType: currentFrameType,
      });
      localStorage.clear();
      navigate(`/payment-success/${oid}`);
    } catch (e) {
      setGiftOrderBusy(false);
      setGiftOrderError(e?.response?.data?.error || "לא הצלחנו להשלים את ההזמנה, נסו שוב");
    }
  };

  // Credits can't reduce the shipping portion — cap by the post-coupon, non-shipping subtotal
  const shippingChargedPortion = (imageCount + imagesBlob.length) >= numberOfImages ? 0 : shippingPrice;
  const maxCreditEligible = Math.max(amountBeforeCredits - shippingChargedPortion - 2, 0);

  // Restore auth from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('pa_token');
    const savedPhone = localStorage.getItem('pa_phone');
    if (savedToken && savedPhone && !auth.isVerified) {
      setAuth({ isVerified: true, phoneNumber: savedPhone, token: savedToken });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch prices when component mounts
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const prices = await getPrices();
        if (prices && prices.length > 0) {
          const priceMap = {};
          prices.forEach(item => {
            priceMap[item.frameType] = item.price;
          });
          setFramePrices(priceMap);
        }
      } catch (error) {
        console.error('Error fetching frame prices:', error);
      }
    };

    fetchPrices();
  }, [setFramePrices]);

  // Fetch credits balance and auto-apply if eligible.
  // Gated on cart hydration: without this, the effect can fire with the
  // atom defaults (netPrice=0, imageCount=0) and cap maxApplicable at
  // shippingPrice-2 instead of the real cart total.
  const totalImages = imageCount + imagesBlob.length;
  const totalNetPrice = netPrice + (imagesBlob.length * framePrice);
  const isCartHydrated = totalImages > 0 && totalNetPrice > 0;
  useEffect(() => {
    if (giftMode) return; // gift cards: no Moments+ credits
    if (auth.isVerified && auth.token && creditsApplied === 0 && isCartHydrated) {
      getCreditsBalance(auth.token)
        .then(res => {
          if (!res.success || res.balance <= 0) return;
          const sessionId = localStorage.getItem('sessionId');
          const maxApplicable = Math.min(res.balance, maxCreditEligible);
          if (sessionId && maxApplicable > 0) {
            setCreditsApplied(maxApplicable);
            setCreditsBalance(res.balance - maxApplicable);
            applyCredits(auth.token, sessionId, maxApplicable)
              .then(r => {
                if (r.success) { setCreditsApplied(r.applied); setCreditsBalance(r.remainingBalance); }
                else { setCreditsApplied(0); setCreditsBalance(res.balance); }
              })
              .catch(() => { setCreditsApplied(0); setCreditsBalance(res.balance); });
          } else {
            setCreditsBalance(res.balance);
          }
        })
        .catch(() => {});
    }
  }, [auth.isVerified, auth.token, isCartHydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleCredits = async () => {
    if (!auth.token) return;
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) return;

    if (creditsApplied > 0) {
      setCreditsLoading(true);
      try {
        const res = await removeCredits(auth.token, sessionId);
        if (res.success) {
          setCreditsApplied(0);
          setCreditsBalance(res.balance);
        }
      } catch (err) {
        console.error('Credits error:', err?.response?.status, err?.response?.data || err);
      } finally {
        setCreditsLoading(false);
      }
    } else {
      const maxApplicable = Math.min(creditsBalance, maxCreditEligible);
      if (maxApplicable <= 0) return;
      setCreditsApplied(maxApplicable);
      setCreditsBalance(creditsBalance - maxApplicable);
      try {
        const res = await applyCredits(auth.token, sessionId, maxApplicable);
        if (res.success) {
          setCreditsApplied(res.applied);
          setCreditsBalance(res.remainingBalance);
        } else {
          setCreditsApplied(0);
          setCreditsBalance(creditsBalance);
        }
      } catch (err) {
        console.error('Credits error:', err?.response?.status, err?.response?.data || err);
        setCreditsApplied(0);
        setCreditsBalance(creditsBalance);
      }
    }
  };

  // Load Tranzila Apple Pay script
  const loadTranzilaScript = () => {
    if (window.hfChargeApple) {
      setIsApplePayScriptLoaded(true);
      return;
    }

    // Check if jQuery is loaded
    const loadMainScript = () => {
      const script = document.createElement('script');
      script.src = `${TRANZILA_SCRIPT_URL}?v=${Date.now()}`;
      script.async = true;

      script.onload = () => {
        console.log("CheckoutMobile: Tranzila Apple Pay script loaded");
        setIsApplePayScriptLoaded(true);
      };

      script.onerror = () => {
        console.error("CheckoutMobile: Failed to load Tranzila script");
      };

      document.body.appendChild(script);
      scriptRef.current = script;
    };

    // Load jQuery first if not present
    if (!window.jQuery) {
      const jqueryScript = document.createElement('script');
      jqueryScript.src = "https://code.jquery.com/jquery-3.6.0.min.js";
      jqueryScript.async = true;
      jqueryScript.onload = loadMainScript;
      document.body.appendChild(jqueryScript);
    } else {
      loadMainScript();
    }
  };

  // Load Apple's SDK so the native <apple-pay-button> element is registered. This is
  // the Apple-recommended button and replaces the old PNG image (which had the
  // centering / broken-image / loading-flash problems). Idempotent + reuses any
  // existing tag so mounting the modal repeatedly doesn't inject duplicate scripts.
  const loadApplePaySdk = () => {
    const markLoaded = () => setAppleSdkLoaded(true);
    if (window.customElements && window.customElements.get('apple-pay-button')) {
      markLoaded();
      return;
    }
    const onReady = () => {
      if (window.customElements && window.customElements.whenDefined) {
        window.customElements.whenDefined('apple-pay-button').then(markLoaded).catch(markLoaded);
      } else {
        markLoaded();
      }
    };
    const existing = document.querySelector('script[data-apple-pay-sdk]');
    if (existing) {
      existing.addEventListener('load', onReady);
      return;
    }
    const script = document.createElement('script');
    script.src = APPLE_PAY_SDK_URL;
    script.async = true;
    script.setAttribute('data-apple-pay-sdk', 'true');
    script.onload = onReady;
    script.onerror = () => console.error('CheckoutMobile: Failed to load Apple Pay SDK');
    document.body.appendChild(script);
    sdkScriptRef.current = script;
  };

  // --- Apple Pay cancel detection + retry reset (Wolt-style) ---

  // Tranzila's hfChargeApple never fires its callback when the user cancels the
  // Apple Pay sheet, which used to leave the button stuck disabled. We additively
  // patch ApplePaySession.begin to also listen for the native 'cancel' event and
  // broadcast it — Tranzila's own oncancel keeps working. If the patch ever fails,
  // the watchdog below still un-sticks the button.
  const patchApplePaySessionCancel = () => {
    try {
      const proto = window.ApplePaySession && window.ApplePaySession.prototype;
      if (!proto || proto.__blendsCancelPatched) return;
      const originalBegin = proto.begin;
      proto.begin = function patchedBegin() {
        try {
          this.addEventListener('cancel', () => {
            window.dispatchEvent(new CustomEvent('blends-applepay-cancel'));
          });
        } catch (e) { /* non-fatal — watchdog covers us */ }
        return originalBegin.apply(this, arguments);
      };
      proto.__blendsCancelPatched = true;
    } catch (e) {
      console.error('CheckoutMobile: could not patch ApplePaySession for cancel detection', e);
    }
  };

  const clearApplePayWatchdog = () => {
    if (applePayWatchdogRef.current) {
      clearTimeout(applePayWatchdogRef.current);
      applePayWatchdogRef.current = null;
    }
  };

  // Re-form the Apple Pay button: fetch a fresh handshake token so a new tap works.
  // Mirrors the reset the error path already used, plus watchdog/session cleanup.
  const resetApplePayForRetry = () => {
    clearApplePayWatchdog();
    applePayResolvedRef.current = true;
    setApplePaySessionActive(false);
    setIsApplePayScriptLoaded(false);
    setApplePayReady(false);
    setPreloadedHandshakeToken(null);
    loadTranzilaScript();
  };

  // Fallback cancel detection: iOS hands focus/visibility back to the page when the
  // Apple Pay sheet closes. If the native 'cancel' event (see the begin patch) didn't
  // fire, this catches the dismissal so the X still shows.
  const beginDismissWatch = () => {
    const openedAt = Date.now();
    let graceTimer = null;
    const onReturn = () => {
      if (applePayResolvedRef.current) return;
      if (document.visibilityState === 'hidden') return; // the sheet opening
      if (Date.now() - openedAt < 400) return;            // ignore open churn
      if (graceTimer) return;
      // Let a real success/error callback win first; otherwise treat as cancel.
      graceTimer = setTimeout(() => {
        graceTimer = null;
        if (applePayResolvedRef.current) return;
        handleApplePayDismissed();
      }, 1200);
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    window.addEventListener('pageshow', onReturn);
    dismissWatchCleanupRef.current = () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
      window.removeEventListener('pageshow', onReturn);
      if (graceTimer) { clearTimeout(graceTimer); graceTimer = null; }
      dismissWatchCleanupRef.current = null;
    };
  };

  const endDismissWatch = () => {
    if (dismissWatchCleanupRef.current) dismissWatchCleanupRef.current();
  };

  // User dismissed the sheet (no payment callback): show the X for a beat, then
  // drop the overlay and re-form the button.
  const handleApplePayDismissed = () => {
    if (applePayResolvedRef.current) return;
    applePayResolvedRef.current = true;
    endDismissWatch();
    clearApplePayWatchdog();
    setApplePaySessionActive(false);
    setApplePayFeedback('error');
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = setTimeout(() => {
      setApplePayFeedback(null);
      resetApplePayForRetry();
    }, 1500);
  };

  // Detect Apple Pay support and preload script
  useEffect(() => {
    if (window.ApplePaySession && isIOS) {
      console.log("CheckoutMobile: Apple Pay is supported on this device");
      setIsApplePaySupported(true);
      patchApplePaySessionCancel();
      loadTranzilaScript();
      loadApplePaySdk();
    }

    // Native 'cancel' from the Apple Pay sheet (see patchApplePaySessionCancel)
    const onApplePayCancel = () => handleApplePayDismissed();
    window.addEventListener('blends-applepay-cancel', onApplePayCancel);

    return () => {
      window.removeEventListener('blends-applepay-cancel', onApplePayCancel);
      clearApplePayWatchdog();
      endDismissWatch();
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
      if (scriptRef.current) {
        try {
          document.body.removeChild(scriptRef.current);
        } catch (err) {
          // Script may have already been removed
        }
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fetch Apple Pay order and handshake token when conditions are met
  // This is REQUIRED because hfChargeApple must be called synchronously from user gesture
  useEffect(() => {
    const preloadApplePayData = async () => {
      // Preload for whichever wallet is available (Apple Pay OR Google Pay) — both
      // need a pre-created order + handshake token (thtk) ready before the tap.
      const walletReady = (isApplePaySupported && isApplePayScriptLoaded) || googlePayEligible;
      if (!walletReady || !addressOk || giftCovers) {
        return;
      }

      console.log("CheckoutMobile: Pre-loading Apple Pay data...");

      try {
        // Gift mode: the GC- order already exists server-side — just handshake.
        if (giftMode) {
          setPreloadedOrderId(giftCheckout.giftOrderId);
          const hs = await axios.get(`${BASE_URL}/apple-pay-handshake?amount=${giftCheckout.amount}`);
          if (hs.data.status === 200 && hs.data.token) {
            setPreloadedHandshakeToken(hs.data.token);
            setApplePayReady(true);
          }
          return;
        }

        // Check/create order
        rememberOrderAccess(); // the success page needs these after localStorage.clear()
        let orderId = localStorage.getItem("orderId");

        if (orderId) {
          const checkRes = await axios.get(`${BASE_URL}/user/checkorder/${orderId}`);
          if (!checkRes.data || checkRes.data.orderStatus !== OrderStatus.NOT_PAID) {
            localStorage.removeItem("orderId");
            orderId = null;
          }
        }

        if (!orderId) {
          console.log("CheckoutMobile: Pre-creating order for Apple Pay");
          const orderData = await axios.post(`${BASE_URL}/gama-createorder`, {
            ...selectedAddress,
            uid: localStorage.getItem('uniqueUserId'),
            sessionId: localStorage.getItem('sessionId'), // key the wall order to the flagged cart (not uid, which can drift)
            finalAmount: amountCharged,
            orderStatus: OrderStatus.NOT_PAID,
            imagesPayed: imageCount + imagesBlob.length,
            frameType: currentFrameType
          });

          if (orderData.data && orderData.data.oid) {
            orderId = orderData.data.oid;
            localStorage.setItem("orderId", orderId);
          }
        }

        if (!orderId) {
          console.error("CheckoutMobile: Failed to pre-create order");
          return;
        }

        setPreloadedOrderId(orderId);

        // Get handshake token
        console.log("CheckoutMobile: Pre-fetching handshake token");
        const handshakeResponse = await axios.get(`${BASE_URL}/apple-pay-handshake?amount=${amountCharged}`);

        if (handshakeResponse.data.status === 200 && handshakeResponse.data.token) {
          setPreloadedHandshakeToken(handshakeResponse.data.token);
          setApplePayReady(true);
          console.log("CheckoutMobile: Apple Pay data pre-loaded successfully");
        }
      } catch (error) {
        console.error("CheckoutMobile: Error pre-loading Apple Pay data:", error);
      }
    };

    preloadApplePayData();
  }, [isApplePaySupported, isApplePayScriptLoaded, googlePayEligible, selectedAddress, amountCharged, imageCount, imagesBlob.length, currentFrameType]);

  const [showAddressLink, setShowAddressLink] = useRecoilState(addressLinkState);
  const [clientCroppedImages] = useRecoilState(clientCroppedImagesState);

  useEffect(() => {
    localStorage.setItem(
      "userShippingAddress",
      JSON.stringify(shippingAddressFormValues)
    );
  }, [shippingAddressFormValues]);

  const queryParams = new URLSearchParams(window.location.search);
  const myPaymentError = queryParams.get("data");

  if (myPaymentError) {
    navigate("/review-your-images");
  }

  const openAddressPopupMobile = () => {
    modal("open", 'addAddressMobile');
  };

  const mySaveCardPopUp = () => {
    if((!selectedAddress || !isAddressComplete(selectedAddress)) && !giftMode){
      return setShowAddressLink(true);
    }
    modal("open", 'selectCardMobile');
  };

  const renderAddAddressButton = () => {
    if (isAddressComplete(selectedAddress)) {
      return {
        class: cn("", { checked: isAddressComplete(selectedAddress) }),
        text:
          selectedAddress.fullName +
          ", " +
          selectedAddress.city,
        img: <img src="/assets/file/images/Check.png" style={{ marginBottom: "2px" }} alt="" />,
      };
    } else {
      return {
        class: "new-link",
        text: "הוספת כתובת למשלוח",
        img: (
          <img
            src="/assets/images/form_address.png"
            style={{ marginLeft: "5px", width: "25px", height: "25px" }}
            alt="form_address"
          />
        ),
      };
    }
  };

  // Debug logging helper - sends logs to server for iPhone debugging (fire and forget)
  const debugLog = (step, data = null, error = null) => {
    axios.post(`${BASE_URL}/apple-pay-debug`, {
      step,
      data,
      error: error ? { message: error.message, stack: error.stack } : null,
      timestamp: new Date().toISOString()
    }).catch(() => {});
  };

  // Handle Apple Pay payment - MUST BE SYNCHRONOUS (no awaits before hfChargeApple)
  // Data is pre-loaded in useEffect above to satisfy Apple Pay's user gesture requirement
  const handleApplePay = () => {
    console.log("CheckoutMobile: Apple Pay button clicked (synchronous handler)");

    // Log to server (fire and forget - no await)
    debugLog("1_BUTTON_CLICKED_SYNC", {
      applePayReady,
      preloadedOrderId,
      hasHandshakeToken: !!preloadedHandshakeToken,
      amountCharged
    });

    // Re-entrancy guard: ignore double-taps while a payment session is open
    if (applePaySessionActive) {
      debugLog("BUTTON_CLICK_IGNORED_SESSION_ACTIVE", { preloadedOrderId });
      return;
    }

    // Check if pre-loaded data is ready
    if (!applePayReady || !preloadedOrderId || !preloadedHandshakeToken) {
      console.error("CheckoutMobile: Apple Pay data not pre-loaded yet");
      debugLog("ERROR_DATA_NOT_PRELOADED", {
        applePayReady,
        preloadedOrderId,
        hasHandshakeToken: !!preloadedHandshakeToken
      });
      return;
    }

    // Check if script is loaded
    if (!window.hfChargeApple) {
      console.error("CheckoutMobile: hfChargeApple not available");
      debugLog("ERROR_SCRIPT_NOT_LOADED");
      loadTranzilaScript();
      return;
    }

    // Invoice enrichment: the terminal auto-issues an invoice per transaction and
    // stamps these charge-time fields onto it (contact/email/phone/address/city →
    // customer; pdesc → product line; uniqueid → the "order number" column). The
    // whole order is a single frame type, so the itemized line is "<frame> ×<count>".
    const framesCount = giftMode ? giftCheckout.frames : imageCount + imagesBlob.length;
    const frameLabel = (frameTypeDisplay[currentFrameType] || frameTypeDisplay["classic"]).text;
    const invoiceProductDesc = giftMode
      ? `גיפט קארד בלנדס ×${framesCount}`
      : `תמונות ממוסגרות – ${frameLabel} ×${framesCount}`;

    // Prepare params - using pre-loaded data
    const applePayParams = {
      labelApple: giftMode ? "גיפט קארד בלנדס" : "תמונות ממוסגרות",
      terminal_name: "blends",
      currency_code: "ILS",
      amount: amountCharged,
      tran_mode: "A",
      thtk: preloadedHandshakeToken,
      response_language: 'english',
      supported_network: ['visa', 'masterCard', 'amex', 'discover'],
      pdesc: invoiceProductDesc,
      // Invoice fields — mapped to the terminal's document columns
      contact: giftMode ? giftCheckout.buyerName : selectedAddress.fullName,
      email: (giftMode ? giftCheckout.buyerEmail : selectedAddress.email) || "",
      phone: giftMode ? giftCheckout.buyerPhone : selectedAddress.phoneNumber,
      address: giftMode ? "" : selectedAddress.address,
      city: giftMode ? "" : selectedAddress.city,
      uniqueid: preloadedOrderId,
      notify_url: `${window.location.origin}/api/apple-pay-webhook?orderId=${preloadedOrderId}`,
      debug: true,
    };

    debugLog("2_CALLING_HFCHARGEAPPLE_SYNC", {
      orderId: preloadedOrderId,
      amount: amountCharged
    });

    // Mark session active so a second tap is ignored until this one resolves
    applePayResolvedRef.current = false;
    setApplePaySessionActive(true);
    setApplePayFeedback('processing');
    beginDismissWatch();

    // Safety net: if neither the payment callback nor a native cancel event ever
    // arrives, un-stick the button after a generous window (real payments, incl.
    // 3DS, resolve well before this).
    clearApplePayWatchdog();
    applePayWatchdogRef.current = setTimeout(() => {
      handleApplePayDismissed();
    }, 150000);

    // Call Apple Pay IMMEDIATELY - no awaits before this!
    // This is called directly from user gesture handler
    window.hfChargeApple(applePayParams, (err, response) => {
      // Session resolved (success or error) — stop the watchdog, allow a fresh attempt
      applePayResolvedRef.current = true;
      clearApplePayWatchdog();
      endDismissWatch();
      setApplePaySessionActive(false);

      debugLog("3_HFCHARGEAPPLE_CALLBACK", {
        hasError: !!err,
        errorMessage: err?.message,
        responseStatus: response?.status,
        fullResponse: response
      });

      if (err) {
        console.error("CheckoutMobile: Apple Pay error", err);
        debugLog("3_CALLBACK_ERROR", null, err);

        // Wolt-style X, then re-form the button for another try
        setApplePayFeedback('error');
        if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = setTimeout(() => {
          setApplePayFeedback(null);
          resetApplePayForRetry();
        }, 1500);
        return;
      }

      // Check for successful transaction - Tranzila returns success in transaction_response
      const isSuccess = response?.transaction_response?.success === true || response?.status === "ok";
      const last4Digits = response?.transaction_response?.credit_card_last_4_digits;

      if (isSuccess) {
        debugLog("4_PAYMENT_SUCCESS", { orderId: preloadedOrderId, amount: amountCharged, last4Digits });

        // Wolt-style success check before navigating
        setApplePayFeedback('success');

        // Gift orders: the webhook GC- branch activates the card — no
        // changeorderstatus; route to the gift success page.
        if (!giftMode) {
          // Update order status in background - include last 4 digits
          axios.post(`${BASE_URL}/user/changeorderstatus/${preloadedOrderId}`, {
            status: OrderStatus.PAID,
            amountCharged: amountCharged,
            clientCroppedImages: clientCroppedImages,
            last4Digits: last4Digits,
            paymentMethod: 'apple_pay'
          }).catch(err => console.error("Error updating order status:", err));
        }

        // Show the check for ~1s, then drop the overlay and navigate.
        const oid = preloadedOrderId;
        const wasGift = giftMode;
        if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = setTimeout(() => {
          setApplePayFeedback(null);
          if (wasGift) {
            setGiftCheckout(null);
            localStorage.removeItem("orderId");
            navigate(`/gift-success/${oid}`);
          } else {
            localStorage.clear();
            navigate(`/payment-success/${oid}`);
          }
        }, 1000);
      } else {
        debugLog("4_PAYMENT_RESPONSE_NOT_OK", { response, isSuccess, transactionResponse: response?.transaction_response });

        // Wolt-style X, then re-form the button for another try
        setApplePayFeedback('error');
        if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = setTimeout(() => {
          setApplePayFeedback(null);
          resetApplePayForRetry();
        }, 1500);
      }
    });

    debugLog("2_HFCHARGEAPPLE_CALLED_WAITING");
  };

  // Google Pay (Tranzila Hosted Fields) — mirrors handleApplePay. Reuses the same
  // preloaded order + handshake token; charges via fields.chargeGpay and, on success,
  // marks the order paid + navigates to success exactly like Apple Pay. The same
  // feedback overlay (processing/success/error) is reused.
  const handleGooglePay = () => {
    console.log("[GPay cart] tap", { hasOrder: !!preloadedOrderId, hasThtk: !!preloadedHandshakeToken });
    // Server-side trace (mobile has no console): proves the tap reached React.
    debugLog("GPAY_CART_1_TAP", { hasOrder: !!preloadedOrderId, hasThtk: !!preloadedHandshakeToken });
    if (!preloadedOrderId || !preloadedHandshakeToken) {
      console.error("CheckoutMobile: Google Pay data not pre-loaded yet");
      debugLog("GPAY_CART_ERR_NOT_PRELOADED");
      return;
    }

    const framesCount = giftMode ? giftCheckout.frames : imageCount + imagesBlob.length;
    const frameLabel = (frameTypeDisplay[currentFrameType] || frameTypeDisplay["classic"]).text;
    const invoiceProductDesc = giftMode
      ? `גיפט קארד בלנדס ×${framesCount}`
      : `תמונות ממוסגרות – ${frameLabel} ×${framesCount}`;

    // No blocking 'processing' overlay here: Google Pay's own sheet is the processing
    // UI, and Tranzila wallet callbacks don't always fire on cancel (see Apple Pay
    // stuck-overlay issue). We only surface success/error from the callback.
    debugLog("GPAY_CART_2_CHARGE_CALLED", { orderId: preloadedOrderId, amount: amountCharged });
    chargeGooglePay({
      terminal_name: "blends",
      response_language: 'english',
      currency_code: "ILS",
      amount: amountCharged,
      tran_mode: "A",
      thtk: preloadedHandshakeToken,
      pdesc: invoiceProductDesc,
      // Invoice fields — mapped to the terminal's document columns (parity with Apple Pay)
      contact: giftMode ? giftCheckout.buyerName : selectedAddress.fullName,
      email: (giftMode ? giftCheckout.buyerEmail : selectedAddress.email) || "",
      phone: giftMode ? giftCheckout.buyerPhone : selectedAddress.phoneNumber,
      address: giftMode ? "" : selectedAddress.address,
      city: giftMode ? "" : selectedAddress.city,
      uniqueid: preloadedOrderId,
      notify_url: `${window.location.origin}/api/google-pay-webhook?orderId=${preloadedOrderId}&method=google_pay`,
    }, (err, response) => {
      console.log("[GPay cart] chargeGpay callback", { err, status: response?.status });
      debugLog("GPAY_CART_3_CALLBACK", { err: err?.message || err || null, status: response?.status });
      if (err) {
        console.error("CheckoutMobile: Google Pay error", err);
        setApplePayFeedback('error');
        if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = setTimeout(() => setApplePayFeedback(null), 1500);
        return;
      }

      const isSuccess = response?.transaction_response?.success === true || response?.status === "ok";
      const last4Digits = response?.transaction_response?.credit_card_last_4_digits;

      if (isSuccess) {
        setApplePayFeedback('success');
        if (!giftMode) {
          axios.post(`${BASE_URL}/user/changeorderstatus/${preloadedOrderId}`, {
            status: OrderStatus.PAID,
            amountCharged: amountCharged,
            clientCroppedImages: clientCroppedImages,
            last4Digits: last4Digits,
            paymentMethod: 'google_pay'
          }).catch(err => console.error("Error updating order status:", err));
        }

        const oid = preloadedOrderId;
        const wasGift = giftMode;
        if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = setTimeout(() => {
          setApplePayFeedback(null);
          if (wasGift) {
            setGiftCheckout(null);
            localStorage.removeItem("orderId");
            navigate(`/gift-success/${oid}`);
          } else {
            localStorage.clear();
            navigate(`/payment-success/${oid}`);
          }
        }, 1000);
      } else {
        setApplePayFeedback('error');
        if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = setTimeout(() => setApplePayFeedback(null), 1500);
      }
    });
  };

  const renderCounts = () => {
    if (giftMode) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>🎁</span>
          <span>x{giftCheckout.frames}</span>
          <span>גיפט קארד בלנדס</span>
        </div>
      );
    }
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <img src={currentFrameDisplay.icon} alt={currentFrameDisplay.text} style={{ width: "20px", height: "20px", objectFit: "contain" }} />
        <span>x{imageCount + imagesBlob.length}</span>
        <span>בלנדס</span>
        <span>{currentFrameDisplay.text} - גודל 21x21</span>
      </div>
    )
  }

  return (
    <div className="mobile-checkout-modal" style={{ ...style }}>
      <AddressLink show={showAddressLink}/>
      <div
        id="mobile-checkout-wrapper"
        style={{ overflowY: "scroll" }}
      >
        <div className="">
          <div className="" id="myCartMobileContent">
            <div className="" style={{ paddingBottom: "0px" }}>
              <div className="van-wrap" style={{ verticalAligh: "middle" }}>
                <span
                  style={{
                    marginRight: "5px",
                    color: "#000000",
                    fontWeight: "400",
                    fontFamily: "rubik",
                    fontSize: "14px",
                    lineHeight: "25px",
                  }}
                >
                <img src="https://www.facebook.com/tr?id=266939557094379&ev=Cart" height="1" width="1" alt=""/>
                  אני רוצה שתארזו לי את המשלוח כמתנה
                </span>&nbsp;
                <input type="checkbox" className="mobile-cart-checkbox" />
                <img src="/assets/images/giftcart.png" style={{ width: "24px", height: "24px" }} alt="" />
              </div>
            </div>

            <div className="modal-body">
              <div style={{ direction: "rtl" }} className="checkout-name-credit-main-div">
                <span>
                  {isAddressComplete(selectedAddress) ? (
                    <p
                      className={renderAddAddressButton().class.toString()}
                      data-dismiss="modal"
                      data-toggle="modal"
                      data-target="#addwin"
                      onClick={openAddressPopupMobile}
                      style={{ paddingTop: "26px", paddingBottom: '16px', fontWeight: 400, fontSize: "14px", verticalAlign: "middle" }}
                    >&nbsp;&nbsp;
                      {renderAddAddressButton().img}
                      &nbsp;&nbsp;&nbsp;&nbsp;
                      {renderAddAddressButton().text}
                    </p>
                  ) : (
                    <p
                      className={renderAddAddressButton().class.toString()}
                      data-dismiss="modal"
                      data-toggle="modal"
                      data-target="#addwin"
                      onClick={openAddressPopupMobile}
                      style={{ paddingTop: "26px", fontWeight: 700, fontSize: "14px" }}
                    >
                      {renderAddAddressButton().img}
                      &nbsp;&nbsp;&nbsp;
                      {renderAddAddressButton().text}
                    </p>
                  )}
                </span>
                <span>
                  <p
                    className="new-link add-padd"
                    data-dismiss="modal"
                    data-toggle="modal"
                    data-target="#addwin"
                    onClick={mySaveCardPopUp}
                    style={{ marginBottom: "10px", fontWeight: 700, fontSize: "14px" }}
                  >
                    <img
                      src="/assets/images/card-pos.svg"
                      alt=""
                    />
                    &nbsp;&nbsp;&nbsp;
                    הוספת אמצעי תשלום
                  </p>
                </span>
              </div>
              <div className="checkout-calculation">
                <div className="checkout-calculation__info" style={{ fontWeight: 400, marginBottom: "0px", justifyContent: "flex-end" }}>
               {imageCount + imagesBlob.length <= 4 && <div>
                  <p>
                    המשלוח צפוי להגיע אליכם עד
                    <span style={{ fontWeight: 500 }}> יום {cartDate}</span>
                  </p>
                                  </div>}
                {imageCount + imagesBlob.length > 4 && <div className="mobile-free-shipping">
                  <p>
                    ההזמנה שלכם זכאית ל
                    <span style={{ fontWeight: 500 }} class="free-shipping-green-text">משלוח חינם,&nbsp;</span>
                    המשלוח צפוי להגיע אליכם עד
                    <span style={{ fontWeight: 500 }} class="free-shipping-green-text"> יום {cartDate}</span>
                  </p>
                  <img src="/assets/images/checkout_check.svg" alt="משלוח חינם" />
                  </div>
                }
                </div>
                

                {/* {(frameChoose === "classic" || frameChoose === "bold") && (
                  <div className="frame-info-message" style={{ 
                    backgroundColor: "#F7F4E9", 
                    padding: "12px 15px", 
                    borderRadius: "8px", 
                    marginBottom: "8px", 
                    marginTop: "15px",
                    fontSize: "14px",
                    fontWeight: "400",
                    textAlign: "right",
                    color: "#9C8558",
                    border: "1px solid #9C8558"
                  }}>
                    {frameChoose === "classic" && (
                      <p style={{ margin: 0, direction: "rtl" }}>
                        ייתכן עיכוב של כשבוע בהגעת המשלוח - <span>"קלאסי"</span> אזל זמנית, חזרה למלאי משוארת ב12.4
                      </p>
                    )}
                    {frameChoose === "bold" && (
                      <p style={{ margin: 0, direction: "rtl" }}>
                        ייתכן עיכוב של כשבוע בהגעת המשלוח - <span>"נועז"</span> אזל זמנית, חזרה למלאי משוארת ב12.4
                      </p>
                    )}
                  </div>
                )} */}
                <InputCoupon imageCount={imageCount + imagesBlob.length} netPrice={netPrice + (imagesBlob.length * framePrice)} />

                <div className="price__table">
                  <div className="price__table--row" style={{ fontSize: "14px", fontWeight: 400 }}>
                    <div>₪ {giftMode ? giftCheckout.amount : netPrice + (imagesBlob.length * framePrice)}</div>
                    <div style={{ direction: "rtl" }}>{renderCounts()}</div>
                  </div>
                  <div className="price__table--row" style={{ fontSize: "14px", fontWeight: 400 }}>
                    <div>₪ {giftMode ? "0.00" : ((imageCount + imagesBlob.length) * handlingFee).toFixed(2)}</div>
                    <div>דמי טיפול</div>
                  </div>
                  <div className="price__table--row" style={{ fontSize: "14px", fontWeight: 400 }}>
                    {giftMode && <div style={{ color: "#B12704" }}>₪&nbsp;0.00</div>}
                    {!giftMode && imageCount + imagesBlob.length <= 4 && <div>₪&nbsp;{shippingPrice}</div>}
                    {!giftMode && imageCount + imagesBlob.length > 4 && <div style={{ color: "#B12704" }}>₪&nbsp;0.00 <span style={{ textDecoration: "line-through", color: "#999", marginRight: "6px" }}>₪{shippingPrice}</span></div>}
                    <div>משלוח</div>
                  </div>
                  {auth.isVerified && (creditsBalance > 0 || creditsApplied > 0) && creditsApplied === 0 && (
                    <div className="price__table--credits">
                      <div className="credits-toggle">
                        <label className="credits-toggle__switch">
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={handleToggleCredits}
                            disabled={creditsLoading}
                          />
                          <span className="credits-toggle__slider" />
                        </label>
                        <span className="credits-toggle__available">&#8362;{creditsBalance.toFixed(2)} {"זמינים"}</span>
                      </div>
                      <div className="credits-toggle__label">
                        <img src="/assets/images/blends-moments-mascot.svg" alt="" className="credits-toggle__mascot" />
                        <span className="credits-toggle__brand-text">Moments+</span>
                      </div>
                    </div>
                  )}
                  <div className={`price__table--row${creditsApplied > 0 ? ' total total--credits' : ''}`} style={{ fontWeight: "700", fontSize: "14px", color: creditsApplied > 0 ? undefined : "#B12704" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", direction: "ltr" }}>
                    <span className={cn({
                      "total__discounted-price": creditsApplied > 0,
                      "total__amount--ploni-mobile": totalFontVariant === "ploni",
                    })}>
                      ₪{Number.isInteger(amountCharged) ? Math.floor(amountCharged) : amountCharged.toFixed(1)}
                    </span>
                    {creditsApplied > 0 && (
                      <span className="total__original-price">₪{amountBeforeCredits.toFixed(2)}</span>
                    )}
                    {creditsApplied > 0 && (
                      <img src="/assets/images/blends-moments-mascot.svg" alt="" className="total__mascot" />
                    )}
                  </div>
                  <div className="totalprice-sum">{"סה\"כ"}</div>
                </div>
                  {creditsApplied > 0 && (
                    <div className={`credits-applied-row${creditsLoading ? ' is-loading' : ''}`}>
                      <img
                        src="/assets/images/blends-moments-mascot.svg"
                        alt=""
                        className="credits-applied-row__mascot"
                      />
                      <span className="credits-applied-row__text">
                        {'חסכת '}<strong>&#8362;{creditsApplied.toFixed(2)}</strong>{' בהזמנה הזו עם '}<bdi dir="ltr">Blends Moments+</bdi>
                      </span>
                      <button
                        className="credits-applied-row__cancel"
                        onClick={handleToggleCredits}
                        disabled={creditsLoading}
                        title="בטל מימוש נקודות"
                      >
                        {creditsLoading ? <span className="credits-applied-row__spinner" /> : '\u00d7'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mobile-checkout-pay-btn-container" style={{ fontSize: "14px" }}>
        {!addressOk && (
          <button
            type="button"
            className="btn cls pay-by-card-button "
            onClick={openAddressPopupMobile}
          >
            בחירת כתובת למשלוח
          </button>
        )}
        {addressOk && (
          giftCovers ? (
            <button
              type="button"
              className="btn cls pay-by-card-button shining-cta"
              onClick={handleGiftCardOrder}
              disabled={giftOrderBusy}
            >
              השלמת ההזמנה
            </button>
          ) : isApplePaySupported ? (
            (applePayReady && appleSdkLoaded) ? (
              /* Apple's native button — it draws itself (correctly sized, centered,
                 localized, crisp on any DPI). No PNG, so no broken-image/centering hacks.
                 Its click still calls hfChargeApple synchronously from the user gesture. */
              <div className="apple-pay-native-wrap">
                {/* Bind the NATIVE onclick via ref (Apple's documented pattern). React 17's
                    synthetic onClick does not reliably fire on this shadow-DOM web component,
                    so taps were silently dropped. Setting node.onclick each render keeps the
                    latest closure and runs synchronously in the user gesture (required so
                    hfChargeApple can open the Apple Pay sheet). */}
                <apple-pay-button
                  ref={(node) => { if (node) node.onclick = handleApplePay; }}
                  buttonstyle="black"
                  type="buy"
                  locale="he-IL"
                ></apple-pay-button>
              </div>
            ) : (
              /* Loading: same footprint as the button — a black rounded bar with a centered
                 white spinner — so nothing shifts when the real button swaps in. */
              <div className="apple-pay-checkout-btn apple-pay-checkout-btn--loading" aria-busy="true">
                <span className="apple-pay-btn-spinner" aria-label="טוען אמצעי תשלום"></span>
              </div>
            )
          ) : (googlePayEligible && !isAndroid) ? (
            /* Apple Pay wins when available; Google Pay only shows when it isn't.
               ANDROID IS GATED OFF: Tranzila's SDK relays the tap to its hidden
               iframe without payment capability delegation, so on Android Chrome
               loadPaymentData rejects (NotAllowedError) and their .catch swallows
               it — sheet never opens, callback never fires. Tranzila-side bug;
               re-enable by removing !isAndroid once they fix it.
               Render the REAL button only once the order + handshake token are
               preloaded — before that a tap would no-op and read as "unclickable",
               so show the same loading bar Apple Pay uses. */
            (preloadedOrderId && preloadedHandshakeToken) ? (
              <button
                type="button"
                className="gpay-express-btn"
                onClick={handleGooglePay}
                aria-label="תשלום עם Google Pay"
              >
                <img src="https://www.gstatic.com/instantbuy/svg/light_gpay.svg" alt="Google Pay" />
              </button>
            ) : (
              <div className="apple-pay-checkout-btn apple-pay-checkout-btn--loading" aria-busy="true">
                <span className="apple-pay-btn-spinner" aria-label="טוען אמצעי תשלום"></span>
              </div>
            )
          ) : (
            <button
              type="button"
              className="btn cls pay-by-card-button shining-cta"
              onClick={mySaveCardPopUp}
            >
              בחירת אמצעי תשלום
            </button>
          )
        )}
        {giftCovers && giftOrderError && (
          <p style={{ color: "#B12704", fontSize: "14px", textAlign: "center", margin: "8px 0 0" }}>{giftOrderError}</p>
        )}
      </div>

      {/* Wolt-style Apple Pay feedback: frosted blur + spinner → check / cross */}
      {applePayFeedback && (
        <div
          className={cn("applepay-feedback", `applepay-feedback--${applePayFeedback}`)}
          role="status"
          aria-live="polite"
        >
          <div className="applepay-feedback__inner">
            {applePayFeedback === 'processing' && (
              <svg className="applepay-feedback__spinner" viewBox="0 0 44 44" aria-hidden="true">
                <circle className="applepay-feedback__spinner-track" cx="22" cy="22" r="18" />
                <circle className="applepay-feedback__spinner-arc" cx="22" cy="22" r="18" />
              </svg>
            )}
            {applePayFeedback === 'success' && (
              <span className="applepay-feedback__badge applepay-feedback__badge--success">
                <svg viewBox="0 0 52 52" className="applepay-feedback__mark">
                  <circle className="applepay-feedback__mark-circle" cx="26" cy="26" r="24" />
                  <path className="applepay-feedback__mark-check" fill="none" d="M15 27 l7 7 l15 -15" />
                </svg>
              </span>
            )}
            {applePayFeedback === 'error' && (
              <span className="applepay-feedback__badge applepay-feedback__badge--error">
                <svg viewBox="0 0 52 52" className="applepay-feedback__mark">
                  <circle className="applepay-feedback__mark-circle" cx="26" cy="26" r="24" />
                  <path className="applepay-feedback__mark-cross" fill="none" d="M18 18 L34 34" />
                  <path className="applepay-feedback__mark-cross" fill="none" d="M34 18 L18 34" />
                </svg>
              </span>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default CheckoutMobile;
